import React, { useState, useEffect, useRef } from 'react';
import Tree from 'react-d3-tree';
import { parseGedcomToTree, convertToD3Tree, findRootPersons, searchPersons } from '../utils/gedcomParser';

const FamilyTree = ({ onClose }) => {
  const [treeData, setTreeData] = useState(null);
  const [gedcomData, setGedcomData] = useState(null);
  const [rootPersons, setRootPersons] = useState([]);
  const [selectedRoot, setSelectedRoot] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const treeContainer = useRef(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  // Load GEDCOM file on component mount
  useEffect(() => {
    loadGedcomFile();
  }, []);

  // Set initial tree position
  useEffect(() => {
    if (treeContainer.current) {
      const dimensions = treeContainer.current.getBoundingClientRect();
      setTranslate({
        x: dimensions.width / 2,
        y: dimensions.height / 4
      });
    }
  }, [treeData]);

  const loadGedcomFile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load the GEDCOM file from public/data folder
      const response = await fetch('/data/rossetti.ged');
      if (!response.ok) {
        throw new Error('Failed to load family tree data');
      }
      
      const content = await response.text();
      const parsed = parseGedcomToTree(content);
      setGedcomData(parsed);
      
      const roots = findRootPersons(parsed);
      setRootPersons(roots);
      
      if (roots.length > 0) {
        // Try to find Pasquale Rossetti as the default root, otherwise use first root
        const pasqualeRoot = roots.find(person => 
          person.name.toLowerCase().includes('pasquale') && 
          person.name.toLowerCase().includes('rossetti')
        );
        const defaultRoot = pasqualeRoot || roots[0];
        
        setSelectedRoot(defaultRoot.id);
        const tree = convertToD3Tree(parsed, defaultRoot.id);
        setTreeData(tree);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading GEDCOM file:', err);
      setError('Failed to load Rossetti family tree data');
      setLoading(false);
    }
  };

  const handleRootChange = (personId) => {
    if (gedcomData && personId) {
      setSelectedRoot(personId);
      const tree = convertToD3Tree(gedcomData, personId);
      setTreeData(tree);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (gedcomData && term) {
      const results = searchPersons(gedcomData, term);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchSelect = (personId) => {
    handleRootChange(personId);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Custom node rendering
  const renderCustomNode = ({ nodeDatum, toggleNode }) => (
    <g>
      <circle
        r={20}
        fill={nodeDatum.attributes?.sex === 'M' ? '#4A90E2' : '#E24A90'}
        stroke="#333"
        strokeWidth="2"
        onClick={toggleNode}
        style={{ cursor: 'pointer' }}
      />
      <text
        fill="white"
        strokeWidth="0"
        x="0"
        y="5"
        textAnchor="middle"
        fontSize="12"
        fontWeight="bold"
      >
        {nodeDatum.name.split(' ')[0]}
      </text>
      <text
        fill="#333"
        strokeWidth="0"
        x="0"
        y="40"
        textAnchor="middle"
        fontSize="10"
        style={{ maxWidth: '120px' }}
      >
        {nodeDatum.name}
      </text>
      {nodeDatum.attributes?.birth && (
        <text
          fill="#666"
          strokeWidth="0"
          x="0"
          y="55"
          textAnchor="middle"
          fontSize="8"
        >
          b. {nodeDatum.attributes.birth}
        </text>
      )}
      {nodeDatum.attributes?.death && (
        <text
          fill="#666"
          strokeWidth="0"
          x="0"
          y="68"
          textAnchor="middle"
          fontSize="8"
        >
          d. {nodeDatum.attributes.death}
        </text>
      )}
    </g>
  );

  if (loading) {
    return (
      <div className="family-tree-modal">
        <div className="family-tree-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading Rossetti family tree...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="family-tree-modal">
        <div className="family-tree-content">
          <div className="error-message">
            <h3>Error Loading Family Tree</h3>
            <p>{error}</p>
            <button onClick={onClose} className="cta-btn primary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="family-tree-modal">
      <div className="family-tree-content">
        <div className="family-tree-header">
          <h2>Rossetti Family Tree</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="family-tree-controls">
          <div className="control-group">
            <label>Root Person:</label>
            <select 
              value={selectedRoot} 
              onChange={(e) => handleRootChange(e.target.value)}
            >
              {rootPersons.map(person => (
                <option key={person.id} value={person.id}>
                  {person.name} {person.birth && `(b. ${person.birth})`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="control-group">
            <label>Search:</label>
            <div className="search-container">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search for a family member..."
              />
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(person => (
                    <div
                      key={person.id}
                      className="search-result"
                      onClick={() => handleSearchSelect(person.id)}
                    >
                      {person.name} {person.birth && `(b. ${person.birth})`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="family-tree-container" ref={treeContainer}>
          {treeData && (
            <Tree
              data={treeData}
              translate={translate}
              orientation="vertical"
              pathFunc="diagonal"
              nodeSize={{ x: 200, y: 150 }}
              separation={{ siblings: 1, nonSiblings: 2 }}
              renderCustomNodeElement={renderCustomNode}
              zoom={0.8}
              scaleExtent={{ min: 0.1, max: 3 }}
              enableLegacyTransitions={true}
            />
          )}
        </div>
        
        <div className="family-tree-legend">
          <div className="legend-item">
            <div className="legend-circle male"></div>
            <span>Male</span>
          </div>
          <div className="legend-item">
            <div className="legend-circle female"></div>
            <span>Female</span>
          </div>
          <p className="legend-note">Click on nodes to expand/collapse branches</p>
        </div>
      </div>
    </div>
  );
};

export default FamilyTree;
